/* eslint-disable react/display-name,react/no-multi-comp */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilterSelectionType } from '../FiltersContainer';
import { ALL_POSTS_TABS } from '../constants';
import {
    Typography,
    Flex,
    TreeSelect,
    Select,
    DatePicker,
    Input,
    Button,
    Row,
    Col,
} from '@app/ui';
import dayjs from 'dayjs';
import '@app/components/filters/components/Filters.scss';

const { SHOW_PARENT } = TreeSelect;
const { RangePicker } = DatePicker;

type AllSocialMediaPageFilters = {
    Accounts: any;
    Caption: any;
    Campaigns: any;
    'Media Type': any;
    startDate: FilterSelectionType | object;
    endDate: FilterSelectionType | object;
};

type Item = {
    value: number | string;
    title: string;
    children?: Item[];
};

const findItemValuePaths =
    (data: Item[]) =>
    (inputValue: number | string): string[] => {
        for (const item of data) {
            // Case 1: input is a parent
            if (`${item.value}` === `${inputValue}`) {
                if (item.children) {
                    return item.children.map(child => `${child.value}`);
                } else {
                    return [`${item.value}`];
                }
            }

            // Case 2: input is a child
            const foundChild = item.children?.find(
                child => child.value === inputValue,
            );
            if (foundChild) {
                return [`${foundChild.value}`];
            }
        }

        // If not found, return empty array
        return [];
    };

type AllSocialMediaFiltersProps = {
    filters: any;
    // eslint-disable-next-line no-unused-vars
    setFilters: (filters: AllSocialMediaPageFilters) => void;
    onUpdateFilters: () => void;
    isPostsLoading: boolean;
    platformAccountsMap: any;
    accountOptions: any;
    currentTab: string;
};

const getMediaTypeOptions = (t?: (key: string) => string) => ({
    1: {
        label: t ? t('filters.mediaType.image') : 'Image',
        value: 1,
    },
    2: {
        label: t ? t('filters.mediaType.video') : 'Video',
        value: 2,
    },
    3: {
        label: t ? t('filters.mediaType.text') : 'Text',
        value: 3,
    },
    4: {
        label: t ? t('filters.mediaType.carousel') : 'Carousel',
        value: 4,
        nested: [
            {
                label: t ? t('filters.mediaType.allImages') : 'All Images',
                value: 1,
            },
            {
                label: t ? t('filters.mediaType.allVideos') : 'All Videos',
                value: 2,
            },
            {
                label: t
                    ? t('filters.mediaType.imagesAndVideos')
                    : 'Images & Videos',
                value: 3,
            },
        ],
    },
});

const AllSocialMediaFilters = ({
    filters,
    setFilters,
    onUpdateFilters,
    isPostsLoading,
    platformAccountsMap,
    accountOptions,
    currentTab,
}: AllSocialMediaFiltersProps) => {
    const { t } = useTranslation('common');
    const mediaTypeOptions = getMediaTypeOptions(t);
    const handleFilterChanged =
        (filterName: string, options: any) => (selectionsChanged: any) => {
            const displayValues = selectionsChanged.map(
                selectionId => options[selectionId].label,
            );
            const truthySelections = {};

            selectionsChanged.forEach(selectionId => {
                truthySelections[selectionId] = true;
            });
            setFilters({
                ...filters,
                [filterName]: {
                    ...filters[filterName],
                    title: filterName,
                    displayValues,
                    value: truthySelections,
                },
            });
        };

    const mediaTypeData = React.useMemo(() => {
        return (
            Object.values(mediaTypeOptions) as Array<{
                label: string;
                value: number;
                nested: Array<{
                    label: string;
                    value: number;
                }>;
            }>
        ).map(option => {
            const newNestedOptions = option.nested?.map(nestedOption => ({
                value: `${option.value}-${nestedOption.value}`,
                title: nestedOption.label,
            }));
            return {
                value: `${option.value}`,
                title: option.label,
                children: newNestedOptions,
            };
        });
    }, [accountOptions]);

    const handleMediaTypesFilterChanged = mediaTypeSelections => {
        const findItems = findItemValuePaths(
            mediaTypeData as unknown as Item[],
        );

        const items = mediaTypeSelections.map(findItems).flat();

        const selectionsValue = items.reduce((acc, selectedAccount) => {
            acc[selectedAccount] = true;
            return acc;
        }, {});
        const displayValues = items.map(genericMt => {
            const [mediaTypeId, carouselType] = genericMt.split('-');
            return carouselType
                ? `${mediaTypeOptions[mediaTypeId].label}-${
                      mediaTypeOptions[mediaTypeId].nested.find(
                          ({ value }) => value == carouselType,
                      ).label
                  }`
                : mediaTypeOptions[mediaTypeId].label;
        });
        const filterValue = items.reduce(
            (acc, selection) => {
                const [mediaTypeId, carouselType] = selection.split('-');
                if (carouselType) acc.carouselMediaTypeIds.push(carouselType);
                else acc.mediaTypeIds.push(mediaTypeId);
                return acc;
            },
            { mediaTypeIds: [], carouselMediaTypeIds: [] },
        );

        setFilters({
            ...filters,
            'Media Type': {
                title: t('filters.mediaTypes'),
                displayValues,
                filterValue,
                value: selectionsValue,
            },
        });
    };

    const accountTreeData = React.useMemo(() => {
        return (
            Object.values(accountOptions) as Array<{
                label: string;
                value: number;
                nested: Array<{
                    label: string;
                    value: number;
                }>;
            }>
        ).map(option => {
            const uniqueChildValues = {};

            const newNestedOptions = option.nested
                ?.filter(nestedOption => {
                    if (uniqueChildValues[nestedOption.value]) return false;
                    uniqueChildValues[nestedOption.value] = true;

                    return true;
                })
                ?.map(nestedOption => ({
                    value: `${option.value}-${nestedOption.value}`,
                    title: nestedOption.label,
                }));

            return {
                value: option.value,
                title: option.label,
                children: newNestedOptions,
            };
        });
    }, [accountOptions]);

    const handleAccountsFilterChanged = accountSelections => {
        const findItems = findItemValuePaths(
            accountTreeData as unknown as Item[],
        );
        const items = accountSelections.map(findItems).flat();

        const selectionsValue = items.reduce((acc, selectedAccount) => {
            acc[selectedAccount] = true;
            return acc;
        }, {});

        const filteredIdsValue = items.reduce((acc, selectedAccount) => {
            const clientAccount = platformAccountsMap[selectedAccount];

            // We want to use filter based on account id.
            // For instagram the filter should use the instagram api account id to call the api.
            const accountId =
                clientAccount.instagramApiAccountId || clientAccount.account.id;
            acc[accountId] = true;
            return acc;
        }, {});

        const displayValues = items.map(
            pAccount => platformAccountsMap[pAccount].account.name,
        );

        setFilters({
            ...filters,
            Accounts: {
                title: t('filters.accounts'),
                displayValues,
                value: filteredIdsValue,
                filterValue: selectionsValue,
            },
        });
    };

    const handleCaptionChanged = event => {
        const newCaptionText = event.target.value;
        const newCaptionFilterValue = filters.Caption;
        newCaptionFilterValue.value = newCaptionText;
        newCaptionFilterValue.displayValues =
            newCaptionText.length > 15
                ? [newCaptionText.slice(0, 15) + '...']
                : [newCaptionText];

        if (newCaptionText.length === 0) {
            newCaptionFilterValue.displayValues = [];
        }

        setFilters({
            ...filters,
            ['Caption']: newCaptionFilterValue,
        });
    };
    const shouldEnableCaptionFilter = currentTab === ALL_POSTS_TABS.ALL_POSTS;
    const dropdownWithSelectAll = (selectAll, hasOptions) => menu => {
        return (
            <>
                {hasOptions && (
                    <div
                        style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                        }}
                        onClick={selectAll}
                    >
                        <Typography.Text style={{ fontWeight: 'bold' }}>
                            {t('filters.selectAll')}
                        </Typography.Text>
                    </div>
                )}
                {menu}
            </>
        );
    };

    const accountValues = Object.entries(filters.Accounts.filterValue).reduce(
        (acc, [k, v]) => {
            if (!v) return acc;

            acc.push(k);
            return acc;
        },
        [],
    );

    const campaignValues = Object.entries(filters.Campaigns.value).reduce(
        (acc, [k, v]) => {
            if (!v) return acc;

            acc.push(parseInt(k));
            return acc;
        },
        [],
    );

    const mediaTypeValues = Object.entries(filters['Media Type'].value).reduce(
        (acc, [k, v]) => {
            if (!v) return acc;

            acc.push(k);
            return acc;
        },
        [],
    );

    const handleClearAllFilters = () => {
        setFilters({
            Accounts: {
                title: t('filters.accounts'),
                value: {},
                displayValues: [],
                filterValue: {},
            },
            Campaigns: {
                title: t('filters.campaigns'),
                value: {},
                displayValues: [],
                options: filters.Campaigns.options, // preserve options
            },
            'Media Type': {
                title: t('filters.mediaTypes'),
                value: {},
                displayValues: [],
                filterValue: {
                    mediaTypeIds: [],
                    carouselMediaTypeIds: [],
                },
            },
            Caption: {
                title: t('filters.caption'),
                value: '',
                displayValues: [],
            },
            startDate: {
                title: t('filters.startDate'),
                value: null,
                displayValues: [],
            },
            endDate: {
                title: t('filters.endDate'),
                value: null,
                displayValues: [],
            },
        });
    };

    const onDateChange = dates => {
        if (dates) {
            setFilters({
                ...filters,
                startDate: {
                    ...filters.startDate,
                    value: dates[0].toDate(),
                    displayValues: [dates[0].format('MM/DD/YYYY')],
                },
                endDate: {
                    ...filters.endDate,
                    value: dates[1].toDate(),
                    displayValues: [dates[1].format('MM/DD/YYYY')],
                },
            });
        } else {
            setFilters({
                ...filters,
                startDate: {
                    ...filters.startDate,
                    value: null,
                    displayValues: [],
                },
                endDate: {
                    ...filters.endDate,
                    value: null,
                    displayValues: [],
                },
            });
        }
    };

    // Check if date range is selected
    const isDateRangeSelected =
        filters.startDate.value && filters.endDate.value;

    return (
        <Flex vertical gap={8} style={{ padding: '16px 0 0 0' }}>
            <Flex gap={16} wrap="wrap">
                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-plt-dropdown"
                    >
                        {t('filters.accounts')}
                    </Typography.Text>

                    <TreeSelect
                        autoClearSearchValue={false}
                        treeCheckable
                        showCheckedStrategy={SHOW_PARENT}
                        placeholder={t('filters.selectAccounts')}
                        treeData={accountTreeData}
                        value={accountValues}
                        onChange={handleAccountsFilterChanged}
                        treeNodeFilterProp="title"
                        showSearch
                        allowClear
                        maxTagCount={2}
                        maxTagTextLength={8}
                        maxTagPlaceholder={`...`}
                        style={{
                            maxHeight: '32px',
                            overflow: 'hidden',
                        }}
                        dropdownRender={dropdownWithSelectAll(
                            () => {
                                handleAccountsFilterChanged(
                                    Object.values(accountOptions),
                                );
                            },
                            Object.values(accountOptions).length > 0,
                        )}
                        disabled={Object.values(accountOptions).length === 0}
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-c-dropdown"
                    >
                        {t('filters.campaigns')}
                    </Typography.Text>
                    <Select
                        placeholder={t('filters.selectCampaigns')}
                        mode="multiple"
                        options={Object.values(filters.Campaigns.options)}
                        value={campaignValues}
                        onChange={handleFilterChanged(
                            'Campaigns',
                            filters.Campaigns.options,
                        )}
                        optionFilterProp="label"
                        allowClear
                        maxTagCount={1}
                        maxTagPlaceholder={`...`}
                        style={{
                            maxHeight: '32px',
                            overflow: 'hidden',
                        }}
                        autoClearSearchValue={false}
                        dropdownRender={dropdownWithSelectAll(
                            () => {
                                handleFilterChanged(
                                    'Campaigns',
                                    filters.Campaigns.options,
                                )(Object.values(filters.Campaigns.options));
                            },
                            Object.values(filters.Campaigns.options).length > 0,
                        )}
                        disabled={
                            Object.values(filters.Campaigns.options).length ===
                            0
                        }
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-c-dropdown"
                    >
                        {t('filters.mediaTypes')}
                    </Typography.Text>
                    <TreeSelect
                        autoClearSearchValue={false}
                        treeCheckable
                        showCheckedStrategy={SHOW_PARENT}
                        placeholder={t('filters.selectMediaTypes')}
                        treeData={mediaTypeData}
                        value={mediaTypeValues}
                        onChange={handleMediaTypesFilterChanged}
                        treeNodeFilterProp="label"
                        showSearch
                        allowClear
                        maxTagCount={2}
                        maxTagTextLength={8}
                        maxTagPlaceholder={`...`}
                        style={{
                            maxHeight: '32px',
                            overflow: 'hidden',
                        }}
                        dropdownRender={dropdownWithSelectAll(
                            () => {
                                handleMediaTypesFilterChanged(
                                    Object.values(mediaTypeOptions),
                                );
                            },
                            Object.values(mediaTypeOptions).length > 0,
                        )}
                        disabled={Object.values(mediaTypeOptions).length === 0}
                    />
                </Flex>
                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="new-ht-dropdown"
                    >
                        {t('filters.caption')}
                    </Typography.Text>
                    <Input
                        value={
                            shouldEnableCaptionFilter
                                ? filters.Caption.value
                                : ''
                        }
                        onChange={handleCaptionChanged}
                        placeholder={t('filters.enterCaption')}
                        data-test="new-ht-dropdown"
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Typography.Text className="-filter-title">
                        {t('filters.dateRange')}
                    </Typography.Text>
                    <RangePicker
                        value={
                            filters.startDate.value && filters.endDate.value
                                ? [
                                      dayjs(filters.startDate.value),
                                      dayjs(filters.endDate.value),
                                  ]
                                : [null, null]
                        }
                        onChange={onDateChange}
                        maxDate={dayjs()}
                    />
                </Flex>
            </Flex>
            <div className="-mvc-divider" />
            <Row gutter={16}>
                <Col span={24}>
                    <Flex gap="small" wrap justify="flex-end" align="flex-end">
                        <Button
                            type="primary"
                            color="default"
                            variant="solid"
                            icon={<i className="fa-regular fa-x -m-r-2" />}
                            onClick={handleClearAllFilters}
                        >
                            {t('filters.clearAllFilters')}
                        </Button>
                        <Button
                            type="primary"
                            color="primary"
                            variant="solid"
                            icon={
                                <i className="fa-regular fa-arrow-rotate-right -m-r-2" />
                            }
                            onClick={() => onUpdateFilters()}
                            disabled={isPostsLoading || !isDateRangeSelected}
                        >
                            {t('filters.updateFilters')}
                        </Button>
                    </Flex>
                </Col>
            </Row>
        </Flex>
    );
};

export default AllSocialMediaFilters;
